import type Big from 'big.js';

import {
  Column,
  TableHeader,
  useDrawerInstance,
  Box,
} from '@cast/design-system';
import { WorkloadTrafficDestinationHistoryItem } from '@cast/types';

import { NetworkResourceType } from 'features/cluster/cost-report/network-cost/_types';
import { CostsCell } from 'features/cost-report/components/network-cost/CostsCell';
import { TrafficCell } from 'features/cost-report/components/network-cost/TrafficCell';
import { WorkloadDetailsLinkCell } from 'features/cost-report/components/WorkloadDetailsLinkCell';

import { sumResource } from '../_utils';

import {
  NoData,
  NoResults,
  SearchableTable,
  SearchableTableRecordsCount,
} from '@cast/shared-search-ui';

type HistoryItem = WorkloadTrafficDestinationHistoryItem<Big>;

export const DestinationsHistoryTable = () => {
  const { close } = useDrawerInstance();

  return (
    <Box flexGrow={1} mx={-32}>
      <SearchableTable
        sizing="l"
        testId="top-destinations-history-table"
        components={{
          noData: <NoData entity="workload" />,
          noResults: <NoResults entity="workload" />,
        }}
        outerHeader={
          <TableHeader
            recordsCount={<SearchableTableRecordsCount title="workload" />}
            px={32}
          />
        }
      >
        <Column<HistoryItem>
          id="workload"
          minWidth={348}
          header="dest workload"
          cellWrapperSx={{ paddingLeft: 32 }}
          sortable
          renderCell={(row) => (
            <WorkloadDetailsLinkCell
              workload={row.destination?.workloadName || ''}
              namespace={row.destination?.namespace || ''}
              workloadType={row.destination?.workloadType || ''}
              onClick={() => close()}
            />
          )}
          accessor={(row) => row.destination?.workloadName || ''}
        />
        <Column<HistoryItem>
          id="traffic"
          minWidth={160}
          header="Total Traffic"
          renderCell={(row) => {
            return (
              <TrafficCell
                egress={
                  row.destination?.egress?.interZoneBytes?.toNumber() || 0
                }
                ingress={
                  row.destination?.ingress?.interZoneBytes?.toNumber() || 0
                }
                stackProps={{ width: 88 }}
              />
            );
          }}
          accessor={(row) => {
            return sumResource(NetworkResourceType.TRAFFIC, row.destination);
          }}
          sortingType="number"
          sortable
        />
        <Column<HistoryItem>
          id="cost"
          minWidth={160}
          header="Total Cost"
          renderCell={(row) => {
            return (
              <CostsCell
                egress={row.destination?.egress?.interZoneCost?.toNumber() || 0}
                ingress={
                  row.destination?.ingress?.interZoneCost?.toNumber() || 0
                }
                stackProps={{ width: 88 }}
              />
            );
          }}
          accessor={(row) => {
            return sumResource(NetworkResourceType.COSTS, row.destination);
          }}
          cellWrapperSx={{ paddingRight: 32 }}
          sortingType="number"
          sortable
        />
      </SearchableTable>
    </Box>
  );
};
